import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getUserData, teamsAPI } from "@/lib/api";
import { Navigation } from "@/components/Navigation";
import { DnaBackground } from "@/components/DnaBackground";
import { Comparison } from "@/components/Comparison";
import { TeamModal } from "@/components/TeamModal";
import { AddMemberModal } from "@/components/AddMemberModal";
import { TeamMembersList } from "@/components/TeamMembersList";
import { ConfirmationModal } from "@/components/ConfirmationModal";
import TeamComparison from "@/components/TeamComparison";
import TeamInsightsChat from "@/components/TeamInsightsChat";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { useTeamInvite } from "@/hooks/use-team-invite";
import { Plus, Users, GitCompare, Edit, Loader2, UserPlus, Trash2, MessageSquare, Link2, Copy } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useToast } from "@/hooks/use-toast";
import { getLocalizedErrorMessage } from "@/lib/error-i18n";

interface Team {
  _id?: string;
  id?: string;
  name: string;
  description?: string;
  createdBy?: string;
  createdAt?: string;
  members?: Array<{
    _id?: string;
    userId?: {
      _id: string;
      name: string;
      email: string;
    };
    role: "member" | "admin";
    joinedAt?: string;
  }>;
}

// Helper function to get team ID (handles both _id and id)
const getTeamId = (team: Team): string => {
  return team._id || team.id || "";
};

export default function Teams() {
  const [user, setUser] = useState<any>(null);
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] = useState(false);
  const [addMemberTeamId, setAddMemberTeamId] = useState<string | null>(null);
  const [editingTeam, setEditingTeam] = useState<Team | null>(null);
  const [confirmDeleteTeam, setConfirmDeleteTeam] = useState<{ open: boolean; team: Team | null }>({
    open: false,
    team: null,
  });
  const [loading, setLoading] = useState(false);
  const [deletingTeamId, setDeletingTeamId] = useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<any>(null);
  const [loadingComparison, setLoadingComparison] = useState(false);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === "ar";
  const { toast } = useToast();
  const { generateInviteLink } = useTeamInvite();
  const [inviteData, setInviteData] = useState<Record<string, any>>({});

  useEffect(() => {
    const userData = getUserData();
    if (!userData) {
      navigate("/auth");
    } else {
      setUser(userData);
    }
  }, [navigate]);

  useEffect(() => {
    if (user) {
      loadTeams();
    }
  }, [user]);

  useEffect(() => {
    if (selectedTeam) {
      loadTeamComparison(selectedTeam);
    }
  }, [selectedTeam]);

  const loadTeams = async () => {
    setLoading(true);
    try {
      const response = await teamsAPI.getMyTeams();
      if (response.success && response.data) {
        console.log("Teams loaded:", response.data);
        setTeams(response.data);
        if (response.data.length > 0 && !selectedTeam) {
          const firstTeamId = getTeamId(response.data[0]);
          setSelectedTeam(firstTeamId);
        }
      }
    } catch (error: any) {
      console.error("Error loading teams:", error);
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "errors.loadTeamsFailed"),
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const loadTeamComparison = async (teamId: string) => {
    setLoadingComparison(true);
    try {
      const response = await teamsAPI.getTeamComparison(teamId);
      if (response.success && response.data) {
        console.log("Team comparison loaded:", response.data);
        setComparisonData(response.data);
      }
    } catch (error: any) {
      console.error("Error loading team comparison:", error);
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "errors.loadTeamComparisonFailed"),
        variant: "destructive",
      });
    } finally {
      setLoadingComparison(false);
    }
  };

  const handleTeamCreated = () => {
    loadTeams();
  };

  const handleEditTeam = (team: Team) => {
    console.log("Editing team:", team);
    const teamToEdit = {
      id: getTeamId(team),
      name: team.name,
      description: team.description
    };
    console.log("Team to edit with ID:", teamToEdit);
    setEditingTeam(teamToEdit);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setEditingTeam(null);
  };

  const handleAddMember = () => {
    if (selectedTeam) {
      setIsAddMemberModalOpen(true);
    }
  };

  const handleMemberAdded = () => {
    // Reload teams to get updated members list
    loadTeams();
    toast({
      title: t("addMember.successTitle"),
      description: t("addMember.memberAddedSuccess"),
    });
  };

  const handleDeleteTeam = async () => {
    if (!confirmDeleteTeam.team) return;

    const teamId = getTeamId(confirmDeleteTeam.team);
    setDeletingTeamId(teamId);

    try {
      const response = await teamsAPI.deleteTeam(teamId);
      if (response.success) {
        toast({
          title: t("teams.teamDeleted"),
          description: t("teams.teamDeletedDesc", { teamName: confirmDeleteTeam.team.name }),
        });

        // Clear selection if deleted team was selected
        if (selectedTeam === teamId) {
          setSelectedTeam(null);
        }

        loadTeams();
      } else {
        throw new Error(response.message);
      }
    } catch (error: any) {
      toast({
        title: t("common.error"),
        description: getLocalizedErrorMessage(error, "errors.deleteTeamFailed"),
        variant: "destructive",
      });
    } finally {
      setDeletingTeamId(null);
      setConfirmDeleteTeam({ open: false, team: null });
    }
  };

  const getTimeLeft = (expiresAt: string) => {
    const diff = new Date(expiresAt).getTime() - new Date().getTime();

    if (diff <= 0) return "Expired";

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff / (1000 * 60 * 60)) % 24);

    if (days > 0) return `${days}d ${hours}h`;
    return `${hours}h`;
  };

  if (!user) return null;

  // Transform comparison data to match TeamComparison component format
  const teamMembersForComparison = comparisonData?.comparisonMatrix?.map((member: any) => ({
    userId: member.userId,
    name: member.name,
    email: member.email,
    role: member.role,
    isCreator: member.isCreator,
    topStrengths: member.topStrengths || []
  })) || [];

  return (
    <div className="min-h-screen overflow-x-hidden" dir={isRtl ? "rtl" : "ltr"}>
      <DnaBackground />
      <Navigation user={user} />

      <main className="relative pt-20 px-4 pb-12 text-start sm:px-6 sm:pt-24">
        <div className="container mx-auto max-w-7xl min-w-0 mb-8 animate-fade-in">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-base text-muted-foreground sm:text-xl">
                {t("teams.subtitle")}
              </p>
            </div>
            <Button className="w-full shrink-0 bg-primary hover:bg-primary/90 shadow-glow sm:w-auto flex items-center gap-2" onClick={() => setIsModalOpen(true)}>
              <Plus className="w-5 h-5" />
              {t("teams.createTeam")}
            </Button>
          </div>
        </div>

        <div className="container mx-auto max-w-7xl min-w-0">
          <Tabs defaultValue="myteams" className="min-w-0 space-y-6" dir={isRtl ? "rtl" : "ltr"}>
            <TabsList className="flex h-auto w-full justify-start gap-1 overflow-x-auto p-1 md:grid md:grid-cols-3 md:overflow-visible">
              <TabsTrigger value="myteams" className="shrink-0 gap-2 px-3 md:shrink">
                <Users className="w-4 h-4" />
                {t("teams.yourTeams")}
              </TabsTrigger>
              <TabsTrigger value="comparison" className="shrink-0 gap-2 px-3 md:shrink">
                <GitCompare className="w-4 h-4" />
                {t("teams.teamComparison")}
              </TabsTrigger>
              <TabsTrigger value="insights" className="shrink-0 gap-2 px-3 md:shrink">
                <MessageSquare className="w-4 h-4" />
                {t("teamInsights.title")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="myteams" className="space-y-6">
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : teams.length > 0 ? (
                <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                  <div className="min-w-0">
                    <h2 className="text-2xl font-bold mb-4">{t("teams.yourTeams")}</h2>
                    <div className="space-y-4">
                      {teams.map((team) => {
                        const teamId = getTeamId(team);
                        const creatorId =
                          typeof team.createdBy === "object"
                            ? (team.createdBy as any)._id
                            : team.createdBy;

                        const isCreator = creatorId === user?.id || creatorId === user?._id;
                        return (
                          <Card
                            key={teamId}
                            className={`p-4 transition-all ${selectedTeam === teamId
                              ? "bg-primary/10 border-primary"
                              : "bg-card hover:bg-muted"
                              }`}
                          >
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                              <div
                                className="min-w-0 flex-1 cursor-pointer"
                                onClick={() => setSelectedTeam(teamId)}
                              >
                                <h3 className="break-words text-lg font-bold">{team.name}</h3>
                                {team.description && (
                                  <p className="break-words text-sm text-muted-foreground mt-1">
                                    {team.description}
                                  </p>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-2">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    console.log("Add member for team:", teamId);
                                    setAddMemberTeamId(teamId);
                                    setIsAddMemberModalOpen(true);
                                  }}
                                  className="ms-2"
                                >
                                  <UserPlus className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEditTeam(team);
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDeleteTeam({ open: true, team });
                                  }}
                                  disabled={deletingTeamId === teamId}
                                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                >
                                  {deletingTeamId === teamId ? (
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-4 h-4" />
                                  )}
                                </Button>
                                {isCreator && (
                                  <DropdownMenu
                                    onOpenChange={async (open) => {
                                      if (open) {
                                        // prevent re-fetch if already loaded
                                        if (inviteData[teamId]?.link) return;

                                        setInviteData((prev) => ({
                                          ...prev,
                                          [teamId]: { loading: true },
                                        }));

                                        const res = await generateInviteLink(teamId);

                                        if (res.success) {
                                          setInviteData((prev) => ({
                                            ...prev,
                                            [teamId]: {
                                              ...res.data,
                                              loading: false,
                                            },
                                          }));
                                        } else {
                                          setInviteData((prev) => ({
                                            ...prev,
                                            [teamId]: { error: true, loading: false },
                                          }));
                                        }
                                      }
                                    }}
                                  >
                                    <DropdownMenuTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <Link2 className="w-4 h-4" />
                                      </Button>
                                    </DropdownMenuTrigger>

                                    <DropdownMenuContent className="p-3 w-64">
                                      {/* 🔄 LOADING */}
                                      {inviteData[teamId]?.loading && (
                                        <div className="flex justify-center py-2">
                                          <Loader2 className="w-4 h-4 animate-spin" />
                                        </div>
                                      )}

                                      {/* ✅ SUCCESS */}
                                      {inviteData[teamId]?.link && (
                                        <div className="space-y-2">
                                          <div className="text-xs text-muted-foreground truncate">
                                            {(() => {
                                              const token =
                                                inviteData[teamId]?.link?.split("/invite/")[1] || "";
                                              return token.length > 20
                                                ? token.slice(0, 20) + "..."
                                                : token;
                                            })()}
                                          </div>

                                          <Button
                                            size="sm"
                                            className="w-full flex gap-2"
                                            onClick={() => {
                                              navigator.clipboard.writeText(inviteData[teamId].link);
                                              toast({
                                                title: "Copied",
                                                description: "Invite link copied to clipboard",
                                              });
                                            }}
                                          >
                                            <Copy className="w-4 h-4" />
                                            Copy
                                          </Button>

                                          <div className="text-xs text-muted-foreground">
                                            Expires in: {getTimeLeft(inviteData[teamId].expiresAt)}
                                          </div>
                                        </div>
                                      )}

                                      {/* ❌ ERROR */}
                                      {inviteData[teamId]?.error && (
                                        <div className="text-xs text-destructive text-center py-2">
                                          Failed to generate link
                                        </div>
                                      )}
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                )}
                              </div>
                            </div>
                          </Card>
                        );
                      })}
                    </div>
                  </div>

                  {selectedTeam && (
                    <div className="min-w-0 space-y-4">
                      <TeamMembersList
                        teamId={selectedTeam}
                        teamName={teams.find(t => getTeamId(t) === selectedTeam)?.name || ""}
                        currentUserId={user.id}
                        members={teams.find(t => getTeamId(t) === selectedTeam)?.members || []}
                        onMemberRemoved={loadTeams}
                      />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    {t("teams.noTeams")}
                  </p>
                </div>
              )}
              <Comparison />
            </TabsContent>

            <TabsContent value="comparison">
              {loadingComparison ? (
                <div className="flex justify-center items-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : selectedTeam && teamMembersForComparison.length > 0 ? (
                <div className="space-y-6">
                  <Card className="p-4 bg-card sm:p-6">
                    <h3 className="break-words text-lg font-bold mb-4 sm:text-xl">
                      {teams.find(t => getTeamId(t) === selectedTeam)?.name || "Team"} - {t("teams.teamComparison")}
                    </h3>
                    {comparisonData?.team && (
                      <div className="flex flex-wrap gap-4 text-sm text-muted-foreground mb-4">
                        <span>{comparisonData.team.memberCount} {comparisonData.team.memberCount === 1 ? t("teams.member") : t("teams.members")}</span>
                      </div>
                    )}
                  </Card>
                  <TeamComparison
                    teamMembers={teamMembersForComparison}
                    sharedStrengths={comparisonData?.sharedStrengths || []}
                    domainCoverage={comparisonData?.domainCoverage || []}
                    showTopStrengths={10}
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    {selectedTeam ? "No comparison data available for this team" : "Please select a team to view comparison"}
                  </p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="insights">
              {selectedTeam ? (
                <TeamInsightsChat
                  teamId={selectedTeam}
                  teamName={teams.find(t => getTeamId(t) === selectedTeam)?.name || ""}
                  comparisonData={comparisonData}
                  members={teams.find(t => getTeamId(t) === selectedTeam)?.members}
                />
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    {t("teamInsights.selectTeam")}
                  </p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>

        <TeamModal
          open={isModalOpen}
          onOpenChange={handleModalClose}
          onTeamCreated={handleTeamCreated}
          userId={user.id}
        />

        <AddMemberModal
          open={isAddMemberModalOpen}
          onOpenChange={(open) => {
            setIsAddMemberModalOpen(open);
            if (!open) setAddMemberTeamId(null);
          }}
          onMemberAdded={handleMemberAdded}
          teamId={addMemberTeamId || ""}
          teamName={teams.find(t => getTeamId(t) === addMemberTeamId)?.name || ""}
        />

        <ConfirmationModal
          open={confirmDeleteTeam.open}
          onOpenChange={(open) => setConfirmDeleteTeam({ open, team: null })}
          onConfirm={handleDeleteTeam}
          title={t("teams.deleteTeamTitle")}
          description={t("teams.deleteTeamDesc", { teamName: confirmDeleteTeam.team?.name || "" })}
          confirmText={t("teams.deleteButton")}
        />
      </main>
    </div>
  );
}
